import React from 'react';
import { BarChart, Bar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './HeroSection.css';
import backgroundImage from '../assets/images/IMG_6076.jpeg';

// Type assertion for recharts components to fix TypeScript compatibility
const PolarAngleAxisComponent = PolarAngleAxis as any;
const PolarRadiusAxisComponent = PolarRadiusAxis as any;

const HeroSection: React.FC = () => {
  // Sample performance data for charts
  const barChartData = [
    { category: 'Technical', score: 7.8 },
    { category: 'Tactical', score: 6.5 },
    { category: 'Physical', score: 8.2 },
    { category: 'Psycho-Social', score: 7.1 },
  ];

  const radarData = [
    { subject: 'Speed', A: 8.5, fullMark: 10 },
    { subject: 'Control', A: 7.2, fullMark: 10 },
    { subject: 'Vision', A: 6.8, fullMark: 10 },
    { subject: 'Strength', A: 7.9, fullMark: 10 },
    { subject: 'Accuracy', A: 8.1, fullMark: 10 },
    { subject: 'Endurance', A: 7.5, fullMark: 10 },
  ];

  const lineChartData = [
    { month: 'Jan', performance: 6.2 },
    { month: 'Feb', performance: 6.8 },
    { month: 'Mar', performance: 7.1 },
    { month: 'Apr', performance: 7.5 },
    { month: 'May', performance: 7.9 },
    { month: 'Jun', performance: 8.2 },
  ];

  // Performance formulas and calculations
  const formulas = [
    { label: 'Weighted Index', formula: 'WI = Σ(Ci × Wi)', value: '7.65' },
    { label: 'Technical Score', formula: 'TS = (FT + PA + BC + UBF + S) / 5', value: '7.8' },
    { label: 'Tactical Score', formula: 'TacS = (FV + DM + P + OBM + T) / 5', value: '6.5' },
    { label: 'Physical Score', formula: 'PS = (S + St + E + CP) / 4', value: '8.2' },
    { label: 'Overall Rating', formula: 'OR = (TS×0.3 + TacS×0.25 + PS×0.3 + PSS×0.15)', value: '7.48' },
  ];

  return (
    <div className="hero-section">
      <div 
        className="hero-background"
        style={{
          backgroundImage: `url(${backgroundImage})`,
        }}
      >
        <div className="hero-image-overlay"></div>
      </div>
      
      {/* Performance Formulas Overlay */}
      <div className="formulas-overlay">
        {formulas.map((formula, index) => (
          <div key={index} className="formula-item" style={{ 
            top: `${15 + index * 15}%`,
            left: index % 2 === 0 ? '5%' : 'auto',
            right: index % 2 === 0 ? 'auto' : '5%',
          }}>
            <div className="formula-label">{formula.label}</div>
            <div className="formula-equation">{formula.formula}</div>
            <div className="formula-value">{formula.value}</div>
          </div>
        ))}
      </div>

      {/* Charts Overlay */}
      <div className="charts-overlay">
        {/* Bar Chart */}
        <div className="chart-container bar-chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(1, 101, 252, 0.2)" />
              <XAxis dataKey="category" stroke="rgba(1, 101, 252, 0.6)" tick={{ fill: 'rgba(1, 101, 252, 0.7)' }} />
              <YAxis stroke="rgba(1, 101, 252, 0.6)" tick={{ fill: 'rgba(1, 101, 252, 0.7)' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(1, 21, 44, 0.9)', 
                  border: '1px solid rgba(1, 101, 252, 0.5)',
                  borderRadius: '8px',
                  color: '#e0e0e0'
                }}
              />
              <Bar dataKey="score" fill="rgba(1, 101, 252, 0.6)" stroke="rgba(1, 101, 252, 0.8)" strokeWidth={2} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Radar Chart */}
        <div className="chart-container radar-chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid stroke="rgba(1, 101, 252, 0.3)" />
              <PolarAngleAxisComponent 
                dataKey="subject" 
                tick={{ fill: 'rgba(1, 101, 252, 0.7)', fontSize: 12 }} 
              />
              <PolarRadiusAxisComponent 
                angle={90} 
                domain={[0, 10]} 
                tick={{ fill: 'rgba(1, 101, 252, 0.5)', fontSize: 10 }} 
              />
              <Radar 
                name="Performance" 
                dataKey="A" 
                stroke="rgba(1, 101, 252, 0.8)" 
                fill="rgba(1, 101, 252, 0.4)" 
                fillOpacity={0.6}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Line Chart */}
        <div className="chart-container line-chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={lineChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(1, 101, 252, 0.2)" />
              <XAxis dataKey="month" stroke="rgba(1, 101, 252, 0.6)" tick={{ fill: 'rgba(1, 101, 252, 0.7)' }} />
              <YAxis stroke="rgba(1, 101, 252, 0.6)" tick={{ fill: 'rgba(1, 101, 252, 0.7)' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(1, 21, 44, 0.9)', 
                  border: '1px solid rgba(1, 101, 252, 0.5)',
                  borderRadius: '8px',
                  color: '#e0e0e0'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="performance" 
                stroke="rgba(1, 101, 252, 0.8)" 
                strokeWidth={3}
                fill="rgba(1, 101, 252, 0.2)"
                fillOpacity={0.3}
                dot={{ fill: 'rgba(1, 101, 252, 0.8)', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;

